/**
 * A collection of frequently used game events.
 * ```ts
 * // Subscribes a callback to the rezise event.
 * App.dispatcher.on(GameEvents.RESIZE, this.onResize, this);
 * 
 * // Unsubscribes a callback from the rezise event.
 * App.dispatcher.off(GameEvents.RESIZE, this.onResize, this);
 * 
 * // Raises the resize event.
 * App.dispatcher.emit(GameEvents.RESIZE);
 *```
 * @export
 * @class GameEvents
 */
export default class GameEvents {
    static REEL_SETS_INITIALIZED = "REEL_SETS_INITIALIZED";
    static ASSETS_LOADED = "ASSETS_LOADED";
    static REMOVED = "removed";
    static SPLASH_CLOSED = "SPLASH_CLOSED";
    static LOADING_SCREEN_COMPLETE = "LOADING_SCREEN_COMPLETE";
    static START_SPIN = "START_SPIN";
    static FINISH_SPIN = "FINISH_SPIN";
    static REEL_STOPPED = "REEL_STOPPED";
    static CASCADE_STOPPED = "CASCADE_STOPPED";
    static REEL_STOPPING = "REEL_STOPPING";
    static ALL_REELS_STOPPED = "ALL_REELS_STOPPED";
    static ALL_REELS_STARTED = "ALL_REELS_STARTED";
    static START_SPINIFICATION = "START_SPINIFICATION";
    static UPDATE_SPINIFICATION = "UPDATE_SPINIFICATION";
    static STOP_SPINIFICATION = "STOP_SPINIFICATION";
    static UI_UPDATE = "UI_UPDATE";
    static LINES_BOX_UPDATE = "LINES_BOX_UPDATE";
    static UPDATE_LAST_WIN = "UPDATE_LAST_WIN";
    static ENTER_IDLE_STATE = "ENTER_IDLE_STATE";
    static START_AUTO_SPIN = "START_AUTO_SPIN";
    static AUTO_SPIN_CHANGED = "AUTO_SPIN_CHANGED";
    static NUM_LINES_UPDATED = "NUM_LINES_UPDATED";
    static START_BONUS = "START_BONUS";
    static SHOW_CHAR_BONUS = "SHOW_CHAR_BONUS";
    static SHOW_CHAR_BONUS_COMPLETE = "SHOW_CHAR_BONUS_COMPLETE";
    static CREATE_BONUS_VIEW = "CREATE_BONUS_VIEW";
    static BONUS_RESULT = "BONUS_RESULT";
    static DESTROY_BONUS_VIEW = "DESTROY_BONUS_VIEW";
    static BONUS_COMPLETE = "BONUS_COMPLETE";
    static NAVIGATE = "NAVIGATE";
    static RESIZE = "RESIZE";
    static SHOW_CHAR_BIG_WIN = "SHOW_CHAR_BIG_WIN";
    static SHOW_CHAR_BIG_WIN_COMPLETE = "SHOW_CHAR_BIG_WIN_COMPLETE";
    /**
     * A win (which is not a big win) has been intercepted manually by the user.
     *
     * @static
     * @memberof GameEvents
     */
    static WIN_INTERCEPTED = "WIN_INTERCEPTED";
    static SHOW_BIG_WIN = "SHOW_BIG_WIN";
    static BIG_WIN_SKIPPED = "BIG_WIN_SKIPPED";
    static SHOW_BIG_WIN_COMPLETE = "SHOW_BIG_WIN_COMPLETE";
    static SHOW_WIN_LINES = "SHOW_WIN_LINES";
    static SHOW_WIN_LINE = "SHOW_WIN_LINE";
    static HIDE_WIN_LINES = "HIDE_WIN_LINES";
    static FORCE_WIN_LINE = "FORCE_WIN_LINE";
    static PAUSE_SHOWING_WIN_LINES = "PAUSE_SHOWING_WIN_LINES";
    static RESUME_SHOWING_WIN_LINES = "RESUME_SHOWING_WIN_LINES";
    static SKIP_SHOWING_WIN_LINES = "SKIP_SHOWING_WIN_LINES";
    static DISABLE_SKIP_WIN_LINES = "DISABLE_SKIP_WIN_LINES";
    static SHOW_WIN_LINES_COMPLETE = "SHOW_WIN_LINES_COMPLETE";
    static OPEN_AUTOPLAY_MENU = "OPEN_AUTOPLAY_MENU";
    static SHOW_CHAR_FREE_SPIN = "SHOW_CHAR_FREE_SPIN";
    static SHOW_CHAR_FREE_SPIN_COMPLETE = "SHOW_CHAR_FREE_SPIN_COMPLETE";
    static SHOW_CHAR_END_FREE_SPIN = 'SHOW_CHAR_END_FREE_SPIN';
    static SHOW_FREE_SPIN_INTRO = "SHOW_FREE_SPIN_INTRO";
    static FREE_SPIN_INTRO_CLOSED = "FREE_SPIN_INTRO_CLOSED";
    static START_FREE_SPIN = "START_FREE_SPIN";
    static SHOW_FREE_SPIN_OUTRO = "SHOW_FREE_SPIN_OUTRO";
    static FREE_SPIN_OUTRO_CLOSED = "FREE_SPIN_OUTRO_CLOSED";
    static FREE_SPINS_WON = "FREE_SPINS_WON";
    static FREE_SPIN_USED = "FREE_SPIN_USED";
    static FREE_SPINS_WON_COMPLETE = "FREE_SPINS_WON_COMPLETE";
    static SHOW_SETTINGS_MENU = "SHOW_SETTINGS_MENU";
    static CLOSE_SETTINGS = "CLOSE_SETTINGS";
    static STOP_SPIN = "STOP_SPIN";
    static SPIN_COMPLETE = "SPIN_COMPLETE";
    static PLAY_STOP_SOUND = "PLAY_STOP_SOUND";
    static SOUND_CHANGED = "SOUND_CHANGED";
    static MUSIC_CHANGED = "MUSIC_CHANGED";
    static ACTIVATE_SPIN = "ACTIVATE_SPIN";
    static END_LOADING = "END_LOADING";
    static PRELOAD_COMPLETE = "PRELOAD_COMPLETE";
    static ENTER_ERROR_STATE = "ENTER_ERROR_STATE";
    static ACTIVATE_PRELOADER_PLAY = "ACTIVATE_PRELOADER_PLAY";
    static GAME_STATE_CHANGED = "GAME_STATE_CHANGED";
    static GAME_STARTED = "GAME_STARTED";
    static CHAINING_ANIMATION_COMPLETE = "CHAINING_ANIMATION_COMPLETE";
    static SYMBOL_ANIM_COMPLETE = "SYMBOL_ANIM_COMPLETE";
    static WIN_ANIMATIONS_COMPLETE = "WIN_ANIM_COMPLETE";
    static GROUP_ANIM_COMPLETE = "GROUP_ANIM_COMPLETE";
    static BANNER_ANIM_COMPLETE = "BANNER_ANIM_COMPLETE";
    static ORIENTATION_CHANGED = "ORIENTATION_CHANGED";
    static GAME_UNPAUSED = "GAME_UNPAUSED";
    static ALL_SYMBOLS_POPPED = "all_symbols_popped";
    static REEL_STOP_STARTED = "reel_stop_started";
    static SHOW_SYMBOL_AT = "SHOW_SYMBOL_AT";
    static HIDE_SYMBOL_AT = "HIDE_SYMBOL_AT";
    static SOUNDS_MUTED = "SOUNDS_MUTED";
    static SOUNDS_UNMUTED = "SOUNDS_UNMUTED";
    static TOGGLE_TURBO_SPIN = "TOGGLE_TURBO_SPIN";
    static SESSION_CREATED = "SESSION_CREATED";

    /**
     * First argument = enter state name
     * 
     * Second argument = exit state name
     *
     * @static
     * @memberof GameEvents
     */
    static STATE_CHANGED = "STATE_CHANGED";

    static ENTER_BONUS_POPUP_OPENED = "ENTER_BONUS_POPUP_OPENED";
    static ENTER_BONUS_POPUP_CLOSED = "ENTER_BONUS_POPUP_CLOSED";
    static EXIT_BONUS_POPUP_OPENED = "EXIT_BONUS_POPUP_OPENED";
    static EXIT_BONUS_POPUP_CLOSED = "EXIT_BONUS_POPUP_CLOSED";

    /**
     * Called when the draw calls has been computed.
     * The argument will be the draw calls count.
     *
     * @static
     * @memberof GameEvents
     */
    static DRAW_CALLS_COMPUTED = "DRAW_CALLS_COMPUTED";

    static COUNTERS_UPDATED = "COUNTERS_UPDATED";

    /**
     * Called, when Howler detects that the audio has been unlocked.
     * Due to the audio unlock is always related to user gesture,
     * we can also request a fullscreen on android.
     *
     * @static
     * @memberof GameEvents
     */
    static AUDIO_UNLOCKED = "AUDIO_UNLOCKED";
}
